import React, { FC, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Tooltip,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Stack,
    Tabs,
    Tab,
    FormControlLabel,
    Checkbox,
    Alert,
    InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import { EASTERN_SIGNS, getEasternSign, getEasternTitle } from '../../../utils/easternZodiac';
import { EASTERN_COLOR_SVGS } from '../../../utils/easternSvgIcons';

export const EasternBadge: FC<{
    signKey: string;
    size?: number;
    showTitle?: boolean;
    fontSize?: number | string;
    shape?: 'squircle' | 'circle';
    mode?: 'colorSvg' | 'kanji' | 'textOnly';
}> = ({ signKey, size = 30, showTitle = true, fontSize = '0.85rem', shape = 'squircle', mode = 'colorSvg' }) => {
    const sign = getEasternSign(signKey);
    if (!sign) return <span>{signKey}</span>;

    const borderRadius = shape === 'squircle' ? Math.max(5, Math.round(size * 0.28)) : '50%';
    const ColorSvgComp = EASTERN_COLOR_SVGS[sign.key];

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, verticalAlign: 'middle' }}>
            {mode !== 'textOnly' && (
                <Box
                    sx={{
                        width: size,
                        height: size,
                        borderRadius: `${borderRadius}px`,
                        background: mode === 'kanji' ? sign.bgGradient : 'transparent',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: mode === 'kanji' ? '0 2px 5px rgba(0,0,0,0.16)' : 'none',
                        userSelect: 'none',
                        flexShrink: 0
                    }}
                    title={`${sign.title} (${sign.elementTitle}) • Годы: ${sign.yearsDescription || ''}`}
                >
                    {mode === 'kanji' ? (
                        <Typography sx={{ fontWeight: 800, fontSize: size <= 26 ? '13px' : '16px', color: '#fff', lineHeight: 1 }}>
                            {sign.kanji}
                        </Typography>
                    ) : ColorSvgComp ? (
                        <ColorSvgComp size={Math.round(size * 0.85)} />
                    ) : (
                        <Typography sx={{ fontSize: size <= 26 ? '14px' : '18px', lineHeight: 1 }}>
                            {sign.symbol}
                        </Typography>
                    )}
                </Box>
            )}
            {showTitle && (
                <Typography component="span" sx={{ fontWeight: 600, fontSize, color: '#263238' }}>
                    {sign.title}
                </Typography>
            )}
        </Box>
    );
};

interface EasternMatrixTableProps {
    easternPairs: Record<string, string>;
    onSavePair: (userSign: string, partnerSign: string, text: string, applyBothWays: boolean) => void;
    onDeletePair: (key: string, reverseKey?: string) => void;
}

export const EasternMatrixTable: FC<EasternMatrixTableProps> = ({
    easternPairs,
    onSavePair,
    onDeletePair
}) => {
    const [viewMode, setViewMode] = useState<'matrix' | 'list'>('matrix');
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Состояние модального окна редактирования
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogUserSign, setDialogUserSign] = useState<string>('tiger');
    const [dialogPartnerSign, setDialogPartnerSign] = useState<string>('horse');
    const [dialogText, setDialogText] = useState<string>('');
    const [applyBothWays, setApplyBothWays] = useState<boolean>(true);

    const [savedNotification, setSavedNotification] = useState<string | null>(null);

    const configuredCount = Object.keys(easternPairs).length;

    const handleOpenEdit = (userSign: string, partnerSign: string) => {
        const key = `${userSign}_${partnerSign}`;
        setDialogUserSign(userSign);
        setDialogPartnerSign(partnerSign);
        setDialogText(easternPairs[key] || '');
        setApplyBothWays(true);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleSaveFromDialog = () => {
        onSavePair(dialogUserSign, dialogPartnerSign, dialogText, applyBothWays);
        const uTitle = getEasternTitle(dialogUserSign);
        const pTitle = getEasternTitle(dialogPartnerSign);
        const msg = dialogText.trim()
            ? `Сохранено: ответ для пары «${uTitle} + ${pTitle}»`
            : `Сохранено: ответ для пары «${uTitle} + ${pTitle}» сброшен`;
        setSavedNotification(msg);
        setDialogOpen(false);
        setTimeout(() => {
            setSavedNotification(null);
        }, 5000);
    };

    const handleDeleteFromDialog = () => {
        const key = `${dialogUserSign}_${dialogPartnerSign}`;
        const reverseKey = `${dialogPartnerSign}_${dialogUserSign}`;
        const uTitle = getEasternTitle(dialogUserSign);
        const pTitle = getEasternTitle(dialogPartnerSign);
        onDeletePair(key, reverseKey);
        setSavedNotification(`Сохранено: ответ для пары «${uTitle} + ${pTitle}» удален`);
        setDialogOpen(false);
        setTimeout(() => {
            setSavedNotification(null);
        }, 5000);
    };

    // Фильтрация списка настроенных пар
    const filteredList = useMemo(() => {
        return Object.entries(easternPairs).filter(([key, text]) => {
            if (searchQuery.trim()) {
                const [u, p] = key.split('_');
                const q = searchQuery.toLowerCase();
                const uTitle = getEasternTitle(u).toLowerCase();
                const pTitle = getEasternTitle(p).toLowerCase();
                const matchSigns = uTitle.includes(q) || pTitle.includes(q);
                const matchText = text.toLowerCase().includes(q);
                return matchSigns || matchText;
            }
            return true;
        });
    }, [easternPairs, searchQuery]);

    const activePairKey = `${dialogUserSign}_${dialogPartnerSign}`;
    const hasActiveText = Boolean(easternPairs[activePairKey]);

    return (
        <Box sx={{ mt: 2 }}>
            {/* Панель управления и переключатель видов */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Tabs
                        value={viewMode}
                        onChange={(e, val) => setViewMode(val)}
                        sx={{
                            minHeight: 36,
                            '& .MuiTab-root': {
                                minHeight: 36,
                                py: 0.5,
                                px: 2,
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                textTransform: 'none'
                            }
                        }}
                    >
                        <Tab value="matrix" label="Китайские настройки" />
                        <Tab
                            value="list"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <span>Настроенные ответы</span>
                                    <Chip
                                        size="small"
                                        label={configuredCount}
                                        color={configuredCount > 0 ? 'primary' : 'default'}
                                        sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700 }}
                                    />
                                </Box>
                            }
                        />
                    </Tabs>
                </Box>
            </Box>

            {savedNotification && (
                <Alert
                    severity="success"
                    onClose={() => setSavedNotification(null)}
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(46,125,50,0.15)',
                        border: '1px solid #81c784',
                        bgcolor: 'rgba(232, 245, 233, 0.9)',
                        alignItems: 'center',
                        fontWeight: 600
                    }}
                >
                    {savedNotification}
                </Alert>
            )}

            {/* ВИД 1: МАТРИЦА СОВМЕСТИМОСТИ ЖИВОТНЫХ 12x12 */}
            {viewMode === 'matrix' && (
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        mb: 2,
                        bgcolor: 'transparent',
                        background: 'transparent',
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                    }}
                >
                    <Box
                        sx={{
                            p: 1.5,
                            bgcolor: 'transparent',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AutoFixHighIcon sx={{ color: '#e65100', fontSize: 20 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#bf360c' }}>
                                Таблица восточного календаря: строки — год пользователя, столбцы — год партнера
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: '0.75rem', color: '#546e7a' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: 'rgba(230, 81, 0, 0.15)', border: '1px solid #ff9800' }} />
                                <span>Настроен ответ</span>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: 'transparent', border: '1px solid #cfd8dc' }} />
                                <span>По умолчанию</span>
                            </Box>
                        </Box>
                    </Box>

                    <TableContainer sx={{ maxHeight: 650, overflowX: 'auto', bgcolor: 'transparent', width: '100%' }}>
                        <Table
                            size="small"
                            stickyHeader
                            sx={{
                                width: '100%',
                                tableLayout: 'fixed',
                                borderCollapse: 'separate',
                                borderSpacing: 0
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            bgcolor: 'rgba(240, 244, 248, 0.92)',
                                            backdropFilter: 'blur(8px)',
                                            zIndex: 4,
                                            position: 'sticky',
                                            left: 0,
                                            top: 0,
                                            width: '8%',
                                            minWidth: '70px',
                                            borderRight: '2px solid rgba(0, 0, 0, 0.12)',
                                            borderBottom: '2px solid rgba(0, 0, 0, 0.15)',
                                            p: 0.5
                                        }}
                                    />
                                    {EASTERN_SIGNS.map(pSign => (
                                        <TableCell
                                            key={pSign.key}
                                            align="center"
                                            sx={{
                                                bgcolor: 'transparent',
                                                p: 0.5,
                                                borderBottom: '2px solid rgba(0, 0, 0, 0.12)',
                                                borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                                                zIndex: 2,
                                                width: '7.6%',
                                                minWidth: '55px'
                                            }}
                                        >
                                            <Tooltip disableInteractive enterDelay={0} leaveDelay={0} TransitionProps={{ timeout: 0 }} PopperProps={{ sx: { pointerEvents: 'none' } }} title={`${pSign.title} (${pSign.elementTitle})`} arrow placement="top">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3, cursor: 'default' }}>
                                                    <EasternBadge signKey={pSign.key} size={24} showTitle={false} />
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.68rem',
                                                            fontWeight: 700,
                                                            color: '#37474f',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: 55
                                                        }}
                                                    >
                                                        {pSign.title}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {EASTERN_SIGNS.map(uSign => (
                                    <TableRow key={uSign.key} hover sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            sx={{
                                                bgcolor: 'transparent',
                                                p: 0.5,
                                                borderRight: '2px solid rgba(0, 0, 0, 0.12)',
                                                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 3
                                            }}
                                        >
                                            <Tooltip disableInteractive enterDelay={0} leaveDelay={0} TransitionProps={{ timeout: 0 }} PopperProps={{ sx: { pointerEvents: 'none' } }} title={`${uSign.title} (${uSign.elementTitle})`} arrow placement="right">
                                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, cursor: 'default' }}>
                                                     <EasternBadge signKey={uSign.key} size={24} showTitle={false} />
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.72rem',
                                                            fontWeight: 700,
                                                            color: '#263238',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                    >
                                                        {uSign.title}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </TableCell>

                                        {EASTERN_SIGNS.map(pSign => {
                                            const pairKey = `${uSign.key}_${pSign.key}`;
                                            const customText = easternPairs[pairKey];
                                            const isConfigured = Boolean(customText);

                                            return (
                                                <TableCell
                                                    key={pSign.key}
                                                    align="center"
                                                    onClick={() => handleOpenEdit(uSign.key, pSign.key)}
                                                    sx={{
                                                        p: 0.3,
                                                        borderRight: '1px solid rgba(0, 0, 0, 0.06)',
                                                        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s ease',
                                                        bgcolor: isConfigured ? 'rgba(230, 81, 0, 0.12)' : 'transparent',
                                                        '&:hover': {
                                                            bgcolor: isConfigured ? 'rgba(230, 81, 0, 0.25)' : 'rgba(230, 81, 0, 0.08)',
                                                            zIndex: 1,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                        }
                                                    }}
                                                >
                                                    <Tooltip
                                                        disableInteractive
                                                        enterDelay={0}
                                                        leaveDelay={0}
                                                        TransitionProps={{ timeout: 0 }}
                                                        PopperProps={{
                                                            sx: { pointerEvents: 'none' }
                                                        }}
                                                        title={
                                                            <Box sx={{ p: 0.5, maxWidth: 280 }}>
                                                                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5, color: '#ffb74d' }}>
                                                                    Пользователь {uSign.title} + партнер {pSign.title}
                                                                </Typography>
                                                                {isConfigured ? (
                                                                    <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
                                                                        «{customText}»
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography sx={{ fontSize: '0.72rem', color: '#b0bec5', fontStyle: 'italic' }}>
                                                                        Ответ по умолчанию (нажмите, чтобы изменить)
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        arrow
                                                    >
                                                        <Box
                                                            sx={{
                                                                height: 38,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: 0.2
                                                            }}
                                                        >
                                                            {isConfigured ? (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: 22,
                                                                        height: 22,
                                                                        borderRadius: '50%',
                                                                        bgcolor: '#e65100',
                                                                        color: '#fff',
                                                                        boxShadow: '0 1px 4px rgba(230,81,0,0.4)'
                                                                    }}
                                                                >
                                                                    <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />
                                                                </Box>
                                                            ) : (
                                                                <Typography sx={{ fontSize: '0.7rem', color: '#b0bec5' }}>
                                                                    —
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* ВИД 2: СПИСОК НАСТРОЕННЫХ ПАР */}
            {viewMode === 'list' && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        borderRadius: 2.5,
                        mb: 2,
                        bgcolor: 'transparent',
                        background: 'transparent',
                        border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                        <TextField
                            size="small"
                            placeholder="Поиск по животным или тексту..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ minWidth: 260 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Typography variant="caption" sx={{ color: '#546e7a', fontWeight: 600 }}>
                            Показано: {filteredList.length} из {configuredCount} настроенных пар
                        </Typography>
                    </Box>

                    {filteredList.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            {configuredCount === 0
                                ? 'Индивидуальные ответы для китайского календаря еще не созданы. Кликните по любой ячейке в таблице «Китайские настройки».'
                                : 'По заданному фильтру ничего не найдено.'}
                        </Alert>
                    ) : (
                        <TableContainer sx={{ maxHeight: 500, borderRadius: 2, bgcolor: 'transparent' }}>
                            <Table size="small" stickyHeader sx={{ bgcolor: 'transparent' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800, width: 220, bgcolor: 'rgba(240, 244, 248, 0.85)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>Животное пользователя</TableCell>
                                        <TableCell sx={{ fontWeight: 800, width: 60, bgcolor: 'rgba(240, 244, 248, 0.85)', backdropFilter: 'blur(8px)', textAlign: 'center', borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}></TableCell>
                                        <TableCell sx={{ fontWeight: 800, width: 220, bgcolor: 'rgba(240, 244, 248, 0.85)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>Животное партнера</TableCell>
                                        <TableCell sx={{ fontWeight: 800, bgcolor: 'rgba(240, 244, 248, 0.85)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>Текст ответа</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, width: 110, bgcolor: 'rgba(240, 244, 248, 0.85)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>Действия</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredList.map(([key, text]) => {
                                        const [u, p] = key.split('_');
                                        return (
                                            <TableRow key={key} hover sx={{ bgcolor: 'transparent' }}>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                                                    <EasternBadge signKey={u} size={28} />
                                                </TableCell>
                                                <TableCell align="center" sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                                                    <SwapHorizIcon sx={{ color: '#90a4ae', fontSize: 20 }} />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                                                    <EasternBadge signKey={p} size={28} />
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                                                    <Tooltip disableInteractive enterDelay={0} leaveDelay={0} TransitionProps={{ timeout: 0 }} PopperProps={{ sx: { pointerEvents: 'none' } }} title={text} arrow placement="top">
                                                        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#263238', lineHeight: 1.35 }}>
                                                            {text}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right" sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenEdit(u, p)}
                                                        title="Редактировать ответ"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => onDeletePair(key, `${p}_${u}`)}
                                                        title="Удалить индивидуальный ответ"
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {/* ДИАЛОГ РЕДАКТИРОВАНИЯ ПАРЫ */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                        Настройка ответа для пары
                    </Typography>
                    <IconButton size="small" onClick={handleCloseDialog}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ py: 2 }}>
                    <Stack spacing={2.5}>
                        {/* Визуальная карточка пары с иконками */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2.5,
                                background: 'linear-gradient(135deg, #f5f9ff 0%, #edf4fc 100%)',
                                border: '1px solid #bbdefb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-around',
                                flexWrap: 'wrap',
                                gap: 1.5
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ display: 'block', color: '#546e7a', fontWeight: 600, mb: 0.5 }}>
                                    Пользователь
                                </Typography>
                                <EasternBadge signKey={dialogUserSign} size={36} fontSize="0.95rem" />
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <SwapHorizIcon sx={{ color: '#1976d2', fontSize: 32 }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                    Пара
                                </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ display: 'block', color: '#546e7a', fontWeight: 600, mb: 0.5 }}>
                                    Партнер
                                </Typography>
                                <EasternBadge signKey={dialogPartnerSign} size={36} fontSize="0.95rem" />
                            </Box>
                        </Paper>

                        {/* Текстовое поле ответа */}
                        <TextField
                            label={`Ответ при проверке для «${getEasternTitle(dialogUserSign)} + ${getEasternTitle(dialogPartnerSign)}»`}
                            multiline
                            minRows={3}
                            maxRows={6}
                            fullWidth
                            value={dialogText}
                            onChange={(e) => setDialogText(e.target.value)}
                            placeholder={`Например: Ваш партнер (${getEasternTitle(dialogPartnerSign)}) с вами несовместим по дате рождения...`}
                            helperText="Если оставить поле пустым или удалить текст, для этой пары будет использоваться общий вердикт несовместимости."
                            autoFocus
                        />

                        {/* Чекбокс симметричности */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={applyBothWays}
                                    onChange={(e) => setApplyBothWays(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#37474f' }}>
                                    Также применить этот ответ для обратной пары (<b>{getEasternTitle(dialogPartnerSign)} + {getEasternTitle(dialogUserSign)}</b>)
                                </Typography>
                            }
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 1.8, justifyContent: 'space-between' }}>
                    <Box>
                        {hasActiveText && (
                            <Button
                                color="error"
                                onClick={handleDeleteFromDialog}
                                startIcon={<DeleteOutlineIcon />}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Удалить ответ
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
                            Отмена
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveFromDialog}
                            startIcon={<CheckCircleIcon />}
                            sx={{ textTransform: 'none', fontWeight: 700, px: 2.5 }}
                        >
                            Сохранить
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EasternMatrixTable;
